import { Injectable } from '@angular/core';
import { Params } from '@angular/router';

export const THI_CAMPAIGN_ATTRIBUTION_KEY = 'thi_campaign_attribution';
export const CAMPAIGN_LANDING_SOURCE = 'campaign_landing';

const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const;

export interface ThiCampaignAttribution {
  source?: string;
  planId?: string;
  referrer?: string;
  capturedAt: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
}

@Injectable({ providedIn: 'root' })
export class ThiCampaignAttributionService {
  captureFromRoute(queryParams: Params): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }
    const existing = this.getStored();
    const next: ThiCampaignAttribution = {
      ...existing,
      capturedAt: existing?.capturedAt ?? new Date().toISOString(),
    };

    let changed = !existing;

    for (const key of UTM_KEYS) {
      const v = queryParams[key];
      if (typeof v === 'string' && v.trim()) {
        next[key] = v.trim();
        changed = true;
      }
    }

    if (typeof queryParams['source'] === 'string' && queryParams['source'].trim()) {
      next.source = queryParams['source'].trim();
      changed = true;
    }

    if (typeof document !== 'undefined' && document.referrer && !next.referrer) {
      next.referrer = document.referrer;
      changed = true;
    }

    if (changed || !existing) {
      try {
        sessionStorage.setItem(THI_CAMPAIGN_ATTRIBUTION_KEY, JSON.stringify(next));
      } catch {
        /* ignore quota */
      }
    }
  }

  getStored(): ThiCampaignAttribution | null {
    if (typeof sessionStorage === 'undefined') {
      return null;
    }
    try {
      const raw = sessionStorage.getItem(THI_CAMPAIGN_ATTRIBUTION_KEY);
      if (!raw) {
        return null;
      }
      return JSON.parse(raw) as ThiCampaignAttribution;
    } catch {
      return null;
    }
  }

  registerQueryParams(planId?: number | string | null): Record<string, string> {
    const params: Record<string, string> = { source: CAMPAIGN_LANDING_SOURCE };
    if (planId != null && String(planId).trim()) {
      params['planId'] = String(planId);
    }
    const stored = this.getStored();
    if (stored) {
      for (const key of UTM_KEYS) {
        const v = stored[key];
        if (v) {
          params[key] = v;
        }
      }
    }
    return params;
  }

  markRegistrationStarted(): void {
    const stored = this.getStored() ?? {
      capturedAt: new Date().toISOString(),
    };
    stored.source = stored.source ?? CAMPAIGN_LANDING_SOURCE;
    try {
      sessionStorage.setItem(THI_CAMPAIGN_ATTRIBUTION_KEY, JSON.stringify(stored));
    } catch {
      /* ignore */
    }
  }
}
